import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { LayoutService } from '../../../services/layout.service';
import { RoutingService } from '../../../services/routing.service';
import { ViewService } from '../../../services/view.service';

@Component({
  selector: 'left-panel',
  imports: [MatIcon],
  templateUrl: './left-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./left-panel.component.scss'],
})
export class LeftPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  private _isExpanded = signal<boolean>(false);
  @Input() set isExpanded(value: boolean) {
    this._isExpanded.set(!!value);
    this._layoutService.hasExtendedLeftPanel.set(!!value);
  }

  get isExpanded() {
    return this._isExpanded();
  }

  public isClosed = input<boolean>(false);

  public $isClosed = computed(
    () =>
      (this._viewService.currentView() === 'map' || !this._viewService.currentView()) &&
      !this._layoutService.hasOpenedLeftPanel()
  );
  //If null, the button is hidden
  public swipePosition = input<'opened' | 'closed' | null>(null);
  @Output() onFullWidth = new EventEmitter();

  public _routingService = inject(RoutingService);
  private _elementRef = inject(ElementRef<HTMLElement>);
  public _layoutService = inject(LayoutService);
  private _viewService = inject(ViewService<string>);

  public currentSwipePostion = this._routingService.$swipePosition;
  public isTransitioning = signal<boolean>(false);
  public isMobile = this._routingService.$isMobile;
  public hasExpandEventListener = false;

  /** Must match the width `transition` duration declared on `:host` in the SCSS. */
  private static readonly WIDTH_TRANSITION_MS = 400;

  private _resizeObserver: ResizeObserver | null = null;
  private _revealTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Target panel width; reused by the host binding and to detect width changes. */
  private _panelWidth = computed(() => {
    if (this.isMobile()) return '100%';
    if (this.isClosed()) return '0';
    return this._isExpanded() ? '100%' : `${LayoutService.LEFT_PANEL_WIDTH}px`;
  });

  constructor() {
    // Hide the projected content while the width animates, then reveal it once
    // the panel is fully open — avoids the content being squeezed mid-transition.
    let previousWidth: string | null = null;
    effect(() => {
      const width = this._panelWidth();
      if (previousWidth !== null && previousWidth !== width) {
        this.hideContentDuringTransition();
      }
      previousWidth = width;
    });

    // Sync the swipe position to the routing service on every input change, so
    // a panel shell that persists across routes (e.g. hosted by a tab around
    // its router outlet) follows per-route values.
    effect(() => {
      this._routingService.$swipePosition.set(this.swipePosition());
    });
  }

  @HostBinding('style.marginTop') get swipeableMargin() {
    if (this.isMobile() && this.currentSwipePostion()) {
      if (this.currentSwipePostion() === 'closed')
        return `calc(100svh - var(--navigation-height) - var(--aside-header-height) -  var(--swipe-zone-height))`;
      else return '0';
    }
  }

  @HostBinding('style.borderRadius') get mobileRadius() {
    if (this.isMobile() && this.currentSwipePostion() === 'closed') return '1.5rem 1.5rem 0 0';
  }

  @HostBinding('style.transform') get closePosition() {
    return this.$isClosed() ? 'translate3d(-100%, 0, 0)' : 'translate3d(0, 0, 0)';
  }

  @HostBinding('style.--panel-width') get width() {
    return this._panelWidth();
  }

  ngOnInit(): void {
    this.hasExpandEventListener = !!this.onFullWidth.observers.length;

    // Trigger isExpanded setter when no value is provided
    if (!this.isExpanded) this.isExpanded = false;
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this._revealTimeout) {
      clearTimeout(this._revealTimeout);
    }
  }

  private setupResizeObserver(): void {
    const resizeObserver = new ResizeObserver(entries => {
      const panelWidth = entries[0].contentRect.width;
      this._layoutService.leftPanelWidth.set(panelWidth);
    });

    resizeObserver.observe(this._elementRef.nativeElement);
    this._resizeObserver = resizeObserver;
  }

  private hideContentDuringTransition(): void {
    this.isTransitioning.set(true);
    if (this._revealTimeout) clearTimeout(this._revealTimeout);
    this._revealTimeout = setTimeout(() => {
      this.isTransitioning.set(false);
      this._revealTimeout = null;
    }, LeftPanelComponent.WIDTH_TRANSITION_MS);
  }

  public toogleMobilePanel() {
    this._routingService.toogleSwipePosition();
  }

  public toggleExpand() {
    this._layoutService.hasExtendedLeftPanel.set(!this._layoutService.hasExtendedLeftPanel());
    this.onFullWidth.emit();
  }
}
